import React, { useMemo, useContext } from "react";
import clsx from "clsx";

import { Context } from "../../../../../../teacherStore";
import { IActiveLearningIssue } from "../../../../../../state";
import { getStudentAnswersByQuestion } from "../../../../../../libs/ActiveLearning";
import AnswerStats from "../../../../../../atoms/ActiveLearning/AnswerStats";
import StudentAnswer from "../../../../../../molecules/ActiveLearning/StudentAnswer";
import styles from "../../../ActiveLearningIssue.module.scss";
import stylesAnswer from "../../../ActiveLearningAnswer.module.scss";

type Issue2AnswerProps = {
  materialCode: string;
  lessonNo: number;
  issue?: IActiveLearningIssue | undefined;
};

export const Issue2Answer = (props: Issue2AnswerProps) => {
  const { state } = useContext(Context);
  const currentIssueNo = 2;

  const question1 = useMemo(() => {
    return props.issue?.questions.find(q => q.order_num === 1);
  }, [props.issue?.questions]);

  const answersSortedByStudentName = useMemo(() => {
    return state.active_learning_answers?.sort((a, b) => {
      return a.last_name_kana.localeCompare(b.last_name_kana, "ja");
    });
  }, [state.active_learning_answers]);

  const studentAnswersQ1 = useMemo(() => {
    const studentAnswers = answersSortedByStudentName?.map(studentAnswers => {
      const filteredAnswers = getStudentAnswersByQuestion(
        props.materialCode,
        studentAnswers,
        props.lessonNo,
        currentIssueNo,
        1,
      );

      return {
        ...studentAnswers,
        answers: filteredAnswers,
      };
    });

    if (!studentAnswers || studentAnswers.length === 0) return [];

    const submittedAnswers = studentAnswers.map(student => ({
      ...student,
      answers: student.answers.map(answer => {
        const matchedStudent = state.classes_by_grade
          .flatMap(student => student.students)
          .find(s => s.id === answer.contents.to_student_id);

        return {
          ...answer,
          contents: {
            student_id: matchedStudent?.id,
            full_name: `${matchedStudent?.last_name} ${matchedStudent?.first_name}`,
            value: answer.contents.value,
          },
        };
      }),
    }));

    return submittedAnswers;
  }, [
    answersSortedByStudentName,
    props.materialCode,
    props.lessonNo,
    state.classes_by_grade,
  ]);

  const studentAnswerStatsQ1 = useMemo(() => {
    const answered =
      studentAnswersQ1.filter(
        studentAnswers => studentAnswers.answers.length > 0,
      ).length || 0;
    const total = studentAnswersQ1.length || 0;
    const unanswered = total - answered;

    return {
      answered,
      total,
      unanswered,
    };
  }, [studentAnswersQ1]);

  return (
    <div key={props.issue?.no} className={styles.issue}>
      <div className={clsx(styles.question, styles.primary)}>
        <div className={styles.header}>
          <span className={styles.no}>
            WORK
            {props.issue?.no && props.issue?.no < 10
              ? `0${props.issue?.no}`
              : props.issue?.no}
          </span>
          <p className={styles.title}>{props.issue?.title}</p>
        </div>
        {question1 && (
          <>
            <div className={styles.themEntry}>
              <p className={styles.title}>{question1.title}</p>
              <AnswerStats
                answered={studentAnswerStatsQ1.answered}
                total={studentAnswerStatsQ1.total}
                unanswered={studentAnswerStatsQ1.unanswered}
              />
            </div>
            <div className={styles.answersArea}>
              {studentAnswersQ1.map((studentAnswers, index) => {
                const no = index + 1;
                const displayNo = no < 10 ? `0${no}` : `${no}`;
                const isAnswered = studentAnswers.answers.length > 0;
                return (
                  <StudentAnswer
                    key={studentAnswers.student_id}
                    isAnswered={isAnswered}
                    isAccordion
                    no={displayNo}
                    name={`${studentAnswers.last_name} ${studentAnswers.first_name}`}
                    classId={studentAnswers.class_id}
                    studentId={studentAnswers.student_id}
                  >
                    {isAnswered && (
                      <div className={styles.answersWrap}>
                        {studentAnswers.answers.map(answers => (
                          <div
                            key={answers.contents.student_id}
                            className={stylesAnswer.tree}
                          >
                            <p className={stylesAnswer.answer}>
                              {answers.contents.full_name}さん
                            </p>
                            <ul className={stylesAnswer.branchList}>
                              <li className={stylesAnswer.branch}>
                                <div className={stylesAnswer.labelWrapper}>
                                  <span className={stylesAnswer.label}>
                                    コメント
                                  </span>
                                </div>
                                <ul className={stylesAnswer.answer}>
                                  <li className={styles.item}>
                                    {answers.contents.value}
                                  </li>
                                </ul>
                              </li>
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}
                  </StudentAnswer>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
export default Issue2Answer;
